import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { DaysOfWeek, QueryParams } from '@/model';
import { ScheduleWeekService } from '../instances/schedule-week';
import { ScheduleWeekConverter } from '@/converters';
import { PostNewsCalendarScheduleWeekRequest, PutNewsCalendarScheduleWeekRequest } from '../request/schedule-week';

export const useScheduleWeek = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_SCHEDULE_WEEK', params],
    async () => {
      const [err, data] = await ScheduleWeekService.getScheduleWeeks(params);
      if (err) {
        throw err;
      }
      const weekSchedule = ScheduleWeekConverter.toDomainFromGetResponse(data);
      const daysOrder = [
        DaysOfWeek.SUNDAY,
        DaysOfWeek.MONDAY,
        DaysOfWeek.TUESDAY,
        DaysOfWeek.WEDNESDAY,
        DaysOfWeek.THURSDAY,
        DaysOfWeek.FRIDAY,
        DaysOfWeek.SATURDAY,
      ];
      // Ordena a lista de weekSchedule pelo campo days_of_week
      const sortedWeekSchedule = (weekSchedule ?? []).sort((a, b) => {
        return daysOrder.indexOf(a.days_of_week) - daysOrder.indexOf(b.days_of_week);
      });
      return sortedWeekSchedule;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePostScheduleWeekMutation = () => {
  return useMutation({
    mutationKey: ['POST_SCHEDULE_WEEK'],
    mutationFn: async (payload: PostNewsCalendarScheduleWeekRequest) => {
      const [err, data] = await ScheduleWeekService.postScheduleWeek(payload);
      if (err) {
        throw err;
      }
      return ScheduleWeekConverter.toDomainFromPostResponse(data);
    },
  });
};

type UsePutScheduleWeekMutationParams = {
  id: string;
  payload: PutNewsCalendarScheduleWeekRequest;
};
export const usePutScheduleWeekMutation = () => {
  return useMutation({
    mutationKey: ['PUT_SCHEDULE_WEEK'],
    mutationFn: async ({ id, payload }: UsePutScheduleWeekMutationParams) => {
      const [err, data] = await ScheduleWeekService.putScheduleWeek(id, payload);
      if (err) {
        throw err;
      }
      return ScheduleWeekConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeleteScheduleWeekMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_SCHEDULE_WEEK'],
    mutationFn: async (id: string) => {
      const [err] = await ScheduleWeekService.deleteScheduleWeek(id);
      if (err) {
        throw err;
      }
    },
  });
};
